import React, { useEffect, useState, useCallback } from "react";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Subscription hook — fetches /api/subscription/status on mount and exposes
 * a refresh() helper. Returns { status, loading, refresh }.
 * Any component can trigger a refresh (e.g. after payment success).
 */
export function useSubscription() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get("/subscription/status");
      setStatus(res.data);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);
  return { status, loading, refresh };
}

/**
 * Start Stripe checkout. Redirects the browser to the Stripe hosted page.
 */
export async function startCheckout() {
  const res = await fetch(`${API}/payments/checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ lookup_key: "pro_monthly", origin_url: window.location.origin }),
  });
  if (!res.ok) throw new Error(`Checkout failed: ${res.status}`);
  const data = await res.json();
  window.location.href = data.checkout_url;
}

/**
 * Poll payment status until paid or timeout.
 */
export async function pollPaymentStatus(sessionId, { onUpdate, maxAttempts = 30, intervalMs = 2000 } = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const res = await fetch(`${API}/payments/status/${sessionId}`);
      const data = await res.json();
      onUpdate?.(data);
      if (data.payment_status === "paid") return { ok: true, data };
      if (["failed", "expired"].includes(data.payment_status)) return { ok: false, data };
    } catch (e) { /* retry */ }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { ok: false, data: { payment_status: "timeout" } };
}
